/**
 * <schmancy-install-steps> — the how-to for the platforms with no API.
 *
 * The only surface in this pair that TEACHES rather than states: on Safari,
 * on Firefox for Android, and on every other browser that installs no WebAPK,
 * the install is a gesture the page can describe and cannot perform. Each of
 * those platforms gets ITS OWN three steps, its own menu, and its own words —
 * iOS's "Add to Home Screen" is never shown to a Mac, whose menu says "Add to
 * Dock" and whose menu is a different menu.
 *
 * The glyph is drawn where the sentence names the control rather than trailing
 * after the line: the reader is matching a mark on their own screen, the
 * vendor is free to reword the menu item between releases, and a glyph parked
 * at the end of a sentence reads as decoration. A schmancy-icon is aria-hidden
 * by construction — the library's icons are decoration and the control that
 * holds one carries the name — so each sentence is split into two halves that
 * still name the control when read straight through with the glyph removed.
 *
 * A real `<ol>`, so the steps announce as "list, 3 items — item 1 of 3". The
 * numerals beside them are then decoration and marked as such.
 *
 * Opened by `openInstallSteps()` and nowhere else. It never opens itself: the
 * fact it teaches stays true on every load until the app IS installed, so a
 * sheet that raised itself would raise itself again next week.
 */
import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { show } from '@schmancy/overlay'
import { html, type TemplateResult } from 'lit'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import type { Observable } from 'rxjs'
import '../button'
import '../icons'
import '../typography'
import { installRoute, type OfferedInstallRoute } from './install-route'

/** One arm per platform, selected by the route — a table rather than a chain
 *  because each arm writes its own three sentences and shares none of them. */
const STEPS = {
	'browser-prompt': () => [],
	'ios-share': () => [
		html`${componentText('installApp.steps.iosShare.share.before')}
			<schmancy-icon class="align-middle text-primary-default">ios_share</schmancy-icon>
			${componentText('installApp.steps.iosShare.share.after')}`,
		html`${componentText('installApp.steps.iosShare.choose')}`,
		html`${componentText('installApp.steps.iosShare.confirm')}`,
	],
	'macos-dock': () => [
		html`${componentText('installApp.steps.macosDock.menu.before')}
			<schmancy-icon class="align-middle text-primary-default">ios_share</schmancy-icon>
			${componentText('installApp.steps.macosDock.menu.after')}`,
		html`${componentText('installApp.steps.macosDock.choose')}`,
		html`${componentText('installApp.steps.macosDock.confirm')}`,
	],
	'menu-shortcut': () => [
		html`${componentText('installApp.steps.menuShortcut.menu')}`,
		html`${componentText('installApp.steps.menuShortcut.choose')}`,
		html`${componentText('installApp.steps.menuShortcut.confirm')}`,
	],
} satisfies Record<OfferedInstallRoute, () => TemplateResult[]>

@schmancyElement('schmancy-install-steps')
export class SchmancyInstallSteps extends SchmancyElement {
	// No `static styles` at all, deliberately: the overlay lays its content out
	// as a grid item and paints the material itself, so a `display: block` host
	// or a ground of this component's own would cover the glass it is sitting
	// on. The caller's `p-6 w-md max-w-full` sizes it.

	override render(): TemplateResult {
		const route = installRoute.value
		const steps = route === 'installed' || route === 'unavailable' ? [] : STEPS[route]()

		return html`
			<div class="grid gap-5">
				<div class="grid gap-1">
					<schmancy-typography type="title" token="lg" weight="medium" class="leading-snug">
						${componentText('installApp.steps.title')}
					</schmancy-typography>
					<schmancy-typography type="body" token="md" class="text-surface-on-variant">
						${componentText('installApp.steps.subtitle')}
					</schmancy-typography>
				</div>

				<ol class="grid gap-4 list-none p-0 m-0">
					${repeat(
						steps,
						(_, index) => index,
						(step, index) => html`
							<li class="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
								<span class="text-sm text-surface-on-variant" aria-hidden="true">${index + 1}.</span>
								<schmancy-typography type="body" token="md" class="leading-snug">${step}</schmancy-typography>
							</li>
						`,
					)}
				</ol>

				${when(
					steps.length > 0,
					() => html`
						<schmancy-button
							variant="filled"
							type="button"
							@click=${() => this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))}
						>
							${componentText('installApp.steps.done')}
						</schmancy-button>
					`,
				)}
			</div>
		`
	}
}

/**
 * The one way this sheet opens. Returns the cold stream `show()` hands back,
 * so the caller owns the subscription and its own `takeUntil` dismisses the
 * sheet if the caller leaves first. Call it from the gesture's own handler —
 * `show()` anchors to the tracked gesture.
 */
export function openInstallSteps(): Observable<unknown> {
	return show(() => html`<schmancy-install-steps class="p-6 w-md max-w-full"></schmancy-install-steps>`, {
		// `role="dialog"` has no name-from-content, and the heading below sits on
		// a different element from the surface that carries the role.
		ariaLabel: componentText('installApp.steps.title'),
	})
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-install-steps': SchmancyInstallSteps
	}
}
